/* [Modified by Codex at 2026-10-15] 让手机与桌面共用量体草稿、五项必填校验及商品订单属性 */
if (!customElements.get('product-custom-size')) {
  customElements.define(
    'product-custom-size',
    class ProductCustomSize extends HTMLElement {
      static drafts = new Map();

      connectedCallback() {
        queueMicrotask(() => {
          if (!this.isConnected || this.initialized) return;
          this.initialize();
        });
      }

      initialize() {
        this.initialized = true;
        this.cleanups = [];
        this.productInfo = this.closest('product-info');
        this.fields = Array.from(this.querySelectorAll('[data-measurement]'));
        this.extraSelect = this.querySelector('[data-extra-length]');
        this.dialog = this.querySelector('[data-measurement-dialog]');
        this.mediaQuery = window.matchMedia('(max-width: 749px)');
        this.renderedValues = new Map();
        this.touched = new Set();
        try {
          this.config = JSON.parse(this.querySelector('[data-custom-size-config]').textContent);
          if (!this.config || typeof this.config !== 'object' || Array.isArray(this.config)) this.config = {};
        } catch (error) {
          this.config = { strings: {} };
        }
        this.strings = this.config.strings || {};
        this.configurationValid = this.readConfiguration();
        this.cacheKey = `${this.dataset.productId}:${this.dataset.sectionId}`;
        this.draft = this.constructor.drafts.get(this.cacheKey) || {
          unit: this.config.defaultUnit === 'cm' ? 'cm' : 'in',
          values: {},
          extraLength: '',
        };
        this.unit = this.draft.unit === 'cm' ? 'cm' : 'in';
        this.restoreInputs();

        this.listen(this, 'input', this.onInput.bind(this));
        this.listen(this, 'change', this.onInput.bind(this));
        this.listen(this, 'click', this.onClick.bind(this));
        this.listen(this, 'focusin', this.onFocus.bind(this));
        this.listen(this, 'pointerover', this.onPointerOver.bind(this));
        this.listen(this, 'pointerout', this.onPointerOut.bind(this));
        this.listen(this, 'keydown', (event) => {
          if (event.key === 'Escape') this.closeHelp();
        });
        this.listen(document, 'click', (event) => {
          if (!this.activeHelp) return;
          const panel = this.querySelector(`[data-help-panel="${this.activeHelp}"]`);
          const button = this.querySelector(`[data-help-toggle="${this.activeHelp}"]`);
          if (!panel?.contains(event.target) && !button?.contains(event.target)) this.closeHelp();
        });
        this.listen(this.mediaQuery, 'change', () => {
          this.closeHelp();
          if (this.dialog?.open) this.dialog.close();
          this.refresh();
        });
        if (this.dialog) {
          this.listen(this.dialog, 'click', (event) => {
            if (event.target === this.dialog) this.dialog.close();
          });
          this.listen(this.dialog, 'close', () => this.querySelector('[data-measurement-zoom]')?.focus());
        }
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cleanups.push(
            subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
              if (this.productInfo?.contains(data.event?.target)) this.refresh();
            }),
            subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
              const sectionId = this.productInfo?.dataset.originalSection || this.dataset.sectionId;
              if (data.sectionId === sectionId) this.refresh();
            })
          );
        }
        this.refresh();
        customElements.whenDefined('product-form').then(() => {
          if (this.isConnected) this.refresh();
        });
      }

      disconnectedCallback() {
        this.cleanups?.forEach((cleanup) => cleanup());
        this.cleanups = [];
        if (this.dialog?.open) this.dialog.close();
        this.initialized = false;
      }

      listen(target, event, handler) {
        target.addEventListener(event, handler);
        this.cleanups.push(() => target.removeEventListener(event, handler));
      }

      readConfiguration() {
        this.ranges = {};
        const keys = ['bust', 'waist', 'hips', 'hollow_to_floor', 'height'];
        let valid = Array.isArray(this.config.customOptionValueIds) && this.config.customOptionValueIds.length > 0;
        keys.forEach((key) => {
          const source = this.config.ranges?.[key];
          const min = this.parseNumber(source?.min);
          const max = this.parseNumber(source?.max);
          if (min === null || max === null || min <= 0 || max < min || !Number.isFinite(min * 254) || !Number.isFinite(max * 254)) valid = false;
          this.ranges[key] = { min, max };
        });
        const options = String(this.config.extraLengths ?? '').split(/\r?\n/).map((value) => value.trim()).filter(Boolean);
        this.extraLengths = [];
        if (!options.length) valid = false;
        options.forEach((option) => {
          const value = this.parseNumber(option);
          if (value === null || value < 0 || !Number.isFinite(value * 254)) valid = false;
          else if (!this.extraLengths.includes(value)) this.extraLengths.push(value);
        });
        return valid && this.fields.length === keys.length && !!this.extraSelect;
      }

      parseNumber(value) {
        const text = String(value ?? '').trim();
        if (!/^\d+(?:[.,]\d+)?$/.test(text)) return null;
        const number = Number(text.replace(',', '.'));
        return Number.isFinite(number) ? number : null;
      }

      formatNumber(value) {
        return String(Math.round((value + Number.EPSILON) * 100) / 100);
      }

      unitLabel() {
        return this.unit === 'cm'
          ? this.strings.unit_short_cm || this.strings.unitCm || 'cm'
          : this.strings.unit_short_in || this.strings.unitIn || 'in';
      }

      formatMessage(template, values = {}) {
        return String(template || '').replace(/\[(measurement|min|max|unit)\]/g, (match, key) => values[key] ?? match);
      }

      restoreInputs() {
        this.fields.forEach((input) => {
          const saved = this.draft.values[input.dataset.measurement];
          if (!saved) input.value = '';
          else if (saved.cm !== null) input.value = this.formatNumber(saved.cm / (this.unit === 'in' ? 2.54 : 1));
          else input.value = saved.raw;
          this.renderedValues.set(input.dataset.measurement, input.value);
        });
        this.renderUnits();
      }

      renderUnits() {
        this.querySelectorAll('[data-unit-toggle]').forEach((button) => {
          button.setAttribute('aria-checked', String(this.unit === 'cm'));
        });
        this.querySelectorAll('[data-unit-text]').forEach((label) => {
          label.classList.toggle('is-active', label.dataset.unitText === this.unit);
        });
        this.querySelectorAll('[data-unit-label]').forEach((label) => {
          label.textContent = `(${this.unitLabel()})`;
        });
        this.fields.forEach((input) => {
          const range = this.ranges[input.dataset.measurement];
          if (range.min === null || range.max === null) return;
          const factor = this.unit === 'cm' ? 2.54 : 1;
          input.placeholder = `${this.formatNumber(range.min * factor)} - ${this.formatNumber(range.max * factor)}`;
        });
        if (!this.extraSelect) return;
        this.extraSelect.replaceChildren();
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = this.strings.extra_length_optional || this.strings.no_extra_length || '';
        this.extraSelect.appendChild(placeholder);
        this.extraLengths.forEach((inches) => {
          const option = document.createElement('option');
          option.value = String(inches);
          option.textContent = inches === 0
            ? this.strings.no_extra_length || '0'
            : this.formatNumber(inches * (this.unit === 'cm' ? 2.54 : 1));
          this.extraSelect.appendChild(option);
        });
        this.extraSelect.value = this.draft.extraLength;
      }

      updateRecord(input) {
        const raw = input.value;
        const number = this.parseNumber(raw);
        const cm = number === null ? null : number * (this.unit === 'in' ? 2.54 : 1);
        this.draft.values[input.dataset.measurement] = {
          raw,
          cm: cm !== null && Number.isFinite(cm * 100) ? cm : null,
        };
        this.renderedValues.set(input.dataset.measurement, raw);
      }

      onInput(event) {
        if (event.target.matches('[data-measurement]')) {
          this.updateRecord(event.target);
          this.touched.add(event.target.dataset.measurement);
        } else if (event.target === this.extraSelect) {
          this.draft.extraLength = this.extraSelect.value;
          this.touched.add('extra_length');
        } else return;
        this.saveDraft();
        this.refresh();
      }

      saveDraft() {
        this.draft.unit = this.unit;
        this.constructor.drafts.set(this.cacheKey, this.draft);
      }

      onClick(event) {
        const unit = event.target.closest('[data-unit-toggle]');
        if (unit && this.contains(unit) && !unit.disabled) {
          this.fields.forEach((input) => {
            if (input.value !== this.renderedValues.get(input.dataset.measurement)) this.updateRecord(input);
          });
          this.unit = this.unit === 'cm' ? 'in' : 'cm';
          this.saveDraft();
          this.restoreInputs();
          this.refresh();
        }
        const help = event.target.closest('[data-help-toggle]');
        if (help && this.contains(help)) {
          const key = help.dataset.helpToggle;
          if (this.activeHelp === key && this.helpPinned) this.closeHelp();
          else {
            this.openHelp(key);
            this.helpPinned = true;
          }
        }
        if (event.target.closest('[data-measurement-zoom]') && this.dialog && !this.dialog.open) {
          this.closeHelp();
          this.dialog.showModal();
        }
        if (event.target.closest('[data-measurement-close]') && this.dialog?.open) this.dialog.close();
      }

      onFocus(event) {
        const input = event.target.closest('[data-measurement], [data-extra-length]');
        const help = event.target.closest('[data-help-toggle]');
        const key = input?.dataset.measurement || (input === this.extraSelect ? 'extra_length' : help?.dataset.helpToggle);
        if (key) {
          const field = key === 'extra_length' ? this.extraSelect : this.fields.find((item) => item.dataset.measurement === key);
          const description = this.querySelector('[data-measurement-help]');
          if (description && field) description.textContent = field.dataset.helpText || '';
          if (this.activeHelp && this.activeHelp !== key) this.closeHelp();
        }
      }

      onPointerOver(event) {
        if (event.pointerType === 'touch' || this.mediaQuery.matches) return;
        const help = event.target.closest('[data-help-toggle]');
        if (help && this.contains(help) && this.activeHelp !== help.dataset.helpToggle) this.openHelp(help.dataset.helpToggle);
      }

      onPointerOut(event) {
        if (!this.activeHelp || this.helpPinned || event.pointerType === 'touch' || this.mediaQuery.matches) return;
        const row = event.target.closest('[data-measurement-row]');
        if (!row || row.contains(event.relatedTarget)) return;
        this.closeHelp();
      }

      openHelp(key) {
        this.closeHelp();
        const panel = this.querySelector(`[data-help-panel="${key}"]`);
        const button = this.querySelector(`[data-help-toggle="${key}"]`);
        if (!panel || !button) return;
        panel.hidden = false;
        button.setAttribute('aria-expanded', 'true');
        this.activeHelp = key;
        const input = key === 'extra_length' ? this.extraSelect : this.fields.find((field) => field.dataset.measurement === key);
        const description = this.querySelector('[data-measurement-help]');
        if (input && description) description.textContent = input.dataset.helpText || '';
      }

      closeHelp() {
        this.querySelectorAll('[data-help-panel]').forEach((panel) => { panel.hidden = true; });
        this.querySelectorAll('[data-help-toggle]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
        this.activeHelp = null;
        this.helpPinned = false;
      }

      isCustomSelected() {
        if (!Array.isArray(this.config.customOptionValueIds) || !this.config.customOptionValueIds.length) return true;
        const picker = this.productInfo?.querySelector('variant-selects');
        const selected = picker?.querySelectorAll('input[data-option-value-id]:checked, option[data-option-value-id]:checked');
        if (!selected?.length) return !!this.config.initialCustom;
        const ids = this.config.customOptionValueIds || [];
        return Array.from(selected).some((option) => ids.includes(String(option.dataset.optionValueId)));
      }

      validate(report = false) {
        let valid = this.configurationValid;
        this.firstInvalid = null;
        this.fields.forEach((input) => {
          const key = input.dataset.measurement;
          if (input.value !== this.renderedValues.get(key)) this.updateRecord(input);
          const value = this.draft.values[key];
          const range = this.ranges[key];
          const factor = this.unit === 'cm' ? 2.54 : 1;
          const min = range.min === null ? null : Number(this.formatNumber(range.min * factor));
          const max = range.max === null ? null : Number(this.formatNumber(range.max * factor));
          const displayedValue = value?.cm === null || !value ? null : Number(this.formatNumber(value.cm / (this.unit === 'in' ? 2.54 : 1)));
          const context = {
            measurement: input.dataset.measurementLabel || key,
            min: min === null ? '' : String(min),
            max: max === null ? '' : String(max),
            unit: this.unitLabel(),
          };
          let message = '';
          if (!input.value.trim()) message = this.formatMessage(this.strings.required || this.strings.incomplete, context);
          else if (!value || value.cm === null) message = this.formatMessage(this.strings.invalid_number, context);
          else if (min === null || max === null || displayedValue < min || displayedValue > max) {
            message = this.formatMessage(this.strings.out_of_range, context);
          }
          const fieldValid = !!input.value.trim() && Number.isFinite(displayedValue) && Number.isFinite(min) && Number.isFinite(max) &&
            displayedValue >= min && displayedValue <= max;
          const showError = !fieldValid && (report || this.touched.has(key));
          input.setAttribute('aria-invalid', String(showError));
          input.classList.toggle('is-invalid', showError);
          const error = this.querySelector(`[data-measurement-error="${key}"]`);
          if (error) {
            error.textContent = showError ? message : '';
            error.hidden = !showError;
          }
          if (!fieldValid && !this.firstInvalid) this.firstInvalid = input;
          valid = valid && fieldValid;
        });
        const extra = this.parseNumber(this.extraSelect?.value);
        const extraValid = this.extraSelect?.value === '' || (extra !== null && this.extraLengths.includes(extra));
        const error = this.querySelector('[data-measurement-error="extra_length"]');
        if (this.extraSelect) this.extraSelect.setAttribute('aria-invalid', String(!extraValid && (report || this.touched.has('extra_length'))));
        if (error) {
          error.hidden = extraValid || !(report || this.touched.has('extra_length'));
          error.textContent = error.hidden ? '' : this.strings.select_extra_length || this.strings.incomplete;
        }
        if (!extraValid && !this.firstInvalid) this.firstInvalid = this.extraSelect;
        return valid && extraValid;
      }

      syncProperties(active, valid) {
        const form = this.getForm();
        const properties = form?.querySelector('[data-custom-size-properties]');
        if (!properties) return;
        const values = {};
        if (active && valid) {
          values.size_type = 'Custom Size';
          values.unit = this.unit.toUpperCase();
          this.fields.forEach((input) => {
            const number = this.draft.values[input.dataset.measurement].cm / (this.unit === 'in' ? 2.54 : 1);
            values[input.dataset.measurement] = `${this.formatNumber(number)} ${this.unit}`;
          });
          values.extra_length = `${this.formatNumber(Number(this.extraSelect.value) * (this.unit === 'cm' ? 2.54 : 1))} ${this.unit}`;
        }
        properties.querySelectorAll('[data-custom-size-property]').forEach((input) => {
          input.value = values[input.dataset.customSizeProperty] || '';
        });
      }

      getForm() {
        return this.productInfo?.querySelector('product-form form[id]')?.getAttribute('id') === this.dataset.formId
          ? this.productInfo.querySelector('product-form form[id]')
          : Array.from(this.productInfo?.querySelectorAll('product-form form[id]') || []).find((form) => form.getAttribute('id') === this.dataset.formId);
      }

      refresh({ report = false } = {}) {
        if (!this.initialized) return false;
        const active = this.isCustomSelected();
        this.hidden = !active;
        this.fields.forEach((input) => { input.disabled = !active || !this.configurationValid; });
        this.querySelectorAll('[data-unit-toggle], [data-extra-length], [data-help-toggle], [data-measurement-zoom]').forEach((control) => {
          control.disabled = !active || !this.configurationValid;
        });
        const measurementsValid = this.validate(report);
        const valid = !active || measurementsValid;
        let message = '';
        if (active) {
          if (!this.configurationValid) message = this.strings.configuration_error || this.dataset.configurationError;
          else message = valid ? this.strings.ready : this.strings.incomplete;
        }
        const status = this.querySelector('[data-custom-size-status]');
        if (status) status.textContent = message || '';
        this.syncProperties(active, valid);
        const productForm = this.getForm()?.closest('product-form');
        productForm?.setCustomSizeState?.({ active, valid, message: active && !valid ? message : '' });
        this.saveDraft();
        if (report && active && !valid && this.configurationValid) this.firstInvalid?.focus();
        return valid;
      }

      prepareSubmission({ report = false } = {}) {
        return this.refresh({ report });
      }
    }
  );
}
/* [End] */

/* [Modified by Codex at 2026-10-15] 尺码表独立于定制量体显示并在当前页面打开所选页面内容 */
if (!customElements.get('product-size-chart')) {
  customElements.define(
    'product-size-chart',
    class ProductSizeChart extends HTMLElement {
      connectedCallback() {
        queueMicrotask(() => {
          if (this.isConnected && !this.onChartClick) this.initializeChart();
        });
      }

      initializeChart() {
        this.chartDialog = this.querySelector('[data-size-chart-dialog]');
        this.chartTrigger = this.querySelector('[data-size-chart-open]');
        if (!this.chartDialog || !this.chartTrigger) return;
        this.onChartClick = (event) => {
          const open = event.target.closest('[data-size-chart-open]');
          const close = event.target.closest('[data-size-chart-close]');
          if (open && this.contains(open) && !this.chartDialog.open) this.chartDialog.showModal();
          if ((close && this.contains(close)) || event.target === this.chartDialog) this.chartDialog.close();
        };
        this.onChartClose = () => {
          if (this.chartTrigger?.isConnected) this.chartTrigger.focus();
        };
        this.addEventListener('click', this.onChartClick);
        this.chartDialog.addEventListener('close', this.onChartClose);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onChartClick);
        this.chartDialog?.removeEventListener('close', this.onChartClose);
        if (this.chartDialog?.open) this.chartDialog.close();
        this.onChartClick = null;
      }
    }
  );
}
/* [End] */
